import type { SuggesterLine, SuggesterStation } from '@metro/suggester'
import type { SchemaData, SchemaLine, SchemaStation } from '../schema/types'
import { lineHexForTheme } from '../schema/lineColor'

/** The cluster key that collapses a multi-line interchange to one row. */
function clusterKey(station: SchemaStation): string {
  return station.slug ?? station.id
}

/** Adapt a schema line into the theme-resolved {@link SuggesterLine}. */
function toSuggesterLine(line: SchemaLine, theme: 'light' | 'dark'): SuggesterLine {
  return { id: line.id, name: line.localName ?? line.id, hexColor: lineHexForTheme(line, theme) }
}

/**
 * Every line serving `primary`'s physical station — the `.line` of every
 * record in `corpus` sharing `primary`'s cluster key (`slug`, falling back
 * to `id`), de-duplicated by line id. This is the web equivalent of the
 * iOS `SuggesterDTOJoin.resolveLines` naptan join (the web adapter
 * collapses interchanges by `slug`, the same grouping used to pick the
 * one displayed row — see {@link buildSuggesterStations}).
 *
 * Ordering (cross-platform contract, mirrors iOS): the PRIMARY line
 * (`primary.line`) is always FIRST; remaining sibling lines follow in
 * ascending record-`id` order (deterministic). A station whose cluster has
 * no other records resolves to exactly `[primary.line]` — the single-line
 * path renders unchanged. Display-only; selection still emits `primary`.
 */
export function resolveLines(
  primary: SchemaStation,
  corpus: readonly SchemaStation[],
  theme: 'light' | 'dark',
): readonly SuggesterLine[] {
  const ordered: SuggesterLine[] = [toSuggesterLine(primary.line, theme)]
  const seen = new Set<string>([primary.line.id])
  const key = clusterKey(primary)
  const siblings = corpus
    .filter((s) => clusterKey(s) === key)
    .slice()
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
  for (const sibling of siblings) {
    if (seen.has(sibling.line.id)) continue
    seen.add(sibling.line.id)
    ordered.push(toSuggesterLine(sibling.line, theme))
  }
  return ordered
}

/**
 * Adapt the metro `SchemaData` into the domain-agnostic
 * {@link SuggesterStation} list `@metro/suggester` consumes.
 *
 * The Suggester package never imports the schema — the host owns the
 * mapping (mirrors how `MobileRouteCardOverlay` resolves line colours
 * from schema for RouteCard).
 *
 * Stations in a multi-line cluster share one `slug`; the Suggester
 * shows ONE row per physical station, so this collapses each cluster to
 * its primary-line record (`primaryLineId`, falling back to the first
 * record encountered). Records without a slug are kept as-is keyed by id
 * (pre-v1.23 schema / malformed fixtures).
 */
export function buildSuggesterStations(
  schema: SchemaData | null,
  theme: 'light' | 'dark',
): readonly SuggesterStation[] {
  if (schema === null) return []

  // Group records by cluster key (slug, or id when slug is absent).
  const clusters = new Map<string, SchemaStation[]>()
  for (const station of schema.stations) {
    const key = clusterKey(station)
    const existing = clusters.get(key)
    if (existing === undefined) {
      clusters.set(key, [station])
    } else {
      existing.push(station)
    }
  }

  const result: SuggesterStation[] = []
  for (const records of clusters.values()) {
    const primary =
      records.find((r) => r.primaryLineId !== undefined && r.line.id === r.primaryLineId) ??
      records[0]
    result.push({
      id: primary.id,
      naptanId: primary.naptanId,
      name: primary.localName,
      lineHexColor: lineHexForTheme(primary.line, theme),
      lineName: primary.line.localName,
      // All lines serving this physical station (display-only multi-line
      // icon + subtitle). `records` is already this station's cluster, so
      // resolveLines' cluster filter is a no-op over it — order + dedupe
      // still apply. `length <= 1` ⇒ the row renders unchanged.
      lines: resolveLines(primary, records, theme),
    })
  }
  return result
}

/**
 * Build the `lineId → { localName, hexColor }` map the Suggester
 * Disruption Details screen resolves its affected-lines chips against
 * (spec §5 / R4). Collected from every station's `line` (the schema
 * carries no top-level lines array — lines hang off station records).
 * `localName` falls back to the line id when absent. An unresolved
 * `lineId` on a disruption is dropped silently by the chip renderer.
 */
export function buildSuggesterLines(
  schema: SchemaData | null,
  theme: 'light' | 'dark',
): Readonly<Record<string, { localName: string; hexColor: string }>> {
  if (schema === null) return {}
  const lines: Record<string, { localName: string; hexColor: string }> = {}
  for (const station of schema.stations) {
    const { id, localName } = station.line
    if (lines[id] === undefined) {
      lines[id] = { localName: localName ?? id, hexColor: lineHexForTheme(station.line, theme) }
    }
  }
  return lines
}
